import React from 'react';
import { Review } from '../types/Review';

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'accent';
  size?: 'sm' | 'md';
  className?: string;
  title?: string; // For hover tooltip
}

export const Badge: React.FC<BadgeProps> = ({ 
  children, 
  variant = 'default', 
  size = 'sm',
  className = '',
  title
}) => {
  return (
    <span
      className={`badge badge-${variant} badge-${size} ${className}`}
      title={title}
    >
      {children}
    </span>
  );
};

// Highlight Count Badge - Clean and professional
export const HighlightCountBadge: React.FC<{ count?: number }> = ({ count = 0 }) => {
  if (count === 0) return null;
  
  const variant = count >= 3 ? 'success' : count >= 1 ? 'info' : 'default';
  
  return (
    <Badge 
      variant={variant}
      title={`${count} key highlight${count !== 1 ? 's' : ''} extracted`}
    >
      {count} highlight{count !== 1 ? 's' : ''}
    </Badge>
  );
};

// Priority Badge - Subtle and elegant
export const PriorityBadge: React.FC<{ priority?: string }> = ({ priority }) => {
  if (!priority || priority === 'STANDARD') return null;
  
  const configs: Record<string, { variant: BadgeProps['variant']; label: string }> = {
    'GOLD': { variant: 'warning', label: 'Gold' },
    'SILVER': { variant: 'info', label: 'Silver' },
    'BRONZE': { variant: 'accent', label: 'Bronze' }
  };
  
  const config = configs[priority];
  if (!config) return null;
  
  return (
    <Badge 
      variant={config.variant}
      title={`${config.label} tier review`}
    >
      {config.label}
    </Badge>
  );
};

// Impact Score Badge - Professional display
export const ImpactScoreBadge: React.FC<{ score?: number }> = ({ score }) => {
  if (!score || score < 40) return null; // Only show for notable scores
  
  let variant: BadgeProps['variant'] = 'default';
  
  if (score >= 80) {
    variant = 'danger';
  } else if (score >= 60) {
    variant = 'warning';
  } else {
    variant = 'info';
  }
  
  return (
    <Badge 
      variant={variant}
      title={`Impact score: ${score}/100`}
    >
      Score: {score}
    </Badge>
  );
};

// Transformation Badge - Simple indicator
export const TransformationBadge: React.FC<{ 
  hasTransformation?: boolean;
  transformation?: Review['transformation_story'];
}> = ({ hasTransformation, transformation }) => {
  if (!hasTransformation) return null;
  
  const title = transformation 
    ? `${transformation.before} → ${transformation.after}`
    : 'Contains transformation story';
  
  return (
    <Badge variant="accent" title={title}>
      Transformation
    </Badge>
  );
};

// Professional Badge - Clean display
export const ProfessionalBadge: React.FC<{ 
  profession?: string;
}> = ({ profession }) => {
  if (!profession) return null;
  
  return (
    <Badge variant="info" title={`Professional: ${profession}`}>
      {profession}
    </Badge>
  );
};

// Verified Badge - Trust indicator
export const VerifiedBadge: React.FC<{ verified?: boolean }> = ({ verified }) => {
  if (!verified) return null;
  
  return (
    <Badge variant="success" size="sm">
      Verified
    </Badge>
  );
};

// Source Badge - Clean platform indicator
export const SourceBadge: React.FC<{ source?: string }> = ({ source }) => {
  if (!source) return null;
  
  const sourceMap: Record<string, string> = {
    'Junip': 'Junip',
    'Meta': 'Meta',
    'Shopify': 'Shopify',
    'direct': 'Direct',
    'api': 'API'
  };
  
  return (
    <Badge variant="default" size="sm">
      {sourceMap[source] || source}
    </Badge>
  );
};

// Sentiment Badge - Professional mood indicator
export const SentimentBadge: React.FC<{ sentiment?: string }> = ({ sentiment }) => {
  if (!sentiment) return null;
  
  const configs: Record<string, { variant: BadgeProps['variant']; label: string }> = {
    'positive': { variant: 'success', label: 'Positive' },
    'negative': { variant: 'danger', label: 'Negative' },
    'neutral': { variant: 'default', label: 'Neutral' },
    'mixed': { variant: 'warning', label: 'Mixed' }
  };
  
  const config = configs[sentiment] || configs.neutral;
  
  return (
    <Badge variant={config.variant}>
      {config.label}
    </Badge>
  );
};

// Composite Badge Group for Review Cards
export const ReviewBadges: React.FC<{ review: Review }> = ({ review }) => {
  const highlightCount = review.highlights?.length || 0;
  const metadata = review.metadata || {};
  
  return (
    <div className="badge-group">
      <HighlightCountBadge count={highlightCount} />
      <PriorityBadge priority={metadata.priority} />
      <ImpactScoreBadge score={metadata.impact_score} />
      <TransformationBadge 
        hasTransformation={metadata.has_transformation}
        transformation={review.transformation_story}
      />
      <ProfessionalBadge profession={metadata.profession} />
      {review.verified_purchase && <VerifiedBadge verified={true} />}
    </div>
  );
};